import React, {useEffect, useState} from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {useTranslation} from 'react-i18next'
import OnboardingApi from '../../Api/onboarding'

type Props = {onSuccess: () => void}

export default function OnboardingStepShipping({onSuccess}: Props) {
  const {t} = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    shipping_type: 'free',
    amount: '0',
    shipping_fixed_price: '',
    from: '1',
    to: '3',
    unit: 'days',
    shipping_policy_page_title: 'Shipping Policy',
    shipping_policy_page_url: 'https://koninstore.com',
  })

  useEffect(() => {
    OnboardingApi.getShipping((res: any) => {
      const data = res?.data
      if (!data) return
      setForm({
        shipping_type: data.shipping_type || 'free',
        amount: String(data.amount ?? 0),
        shipping_fixed_price: data.shipping_fixed_price != null ? String(data.shipping_fixed_price) : '',
        from: String(data.from || 1),
        to: String(data.to || 3),
        unit: data.unit || 'days',
        shipping_policy_page_title: data.shipping_policy_page_title || 'Shipping Policy',
        shipping_policy_page_url: data.shipping_policy_page_url || 'https://koninstore.com',
      })
    }, setIsLoading)
  }, [])

  const submit = () => {
    setError('')
    const from = Number(form.from)
    const to = Number(form.to)
    if (!form.shipping_type || from < 1 || to < from) {
      setError(t('onboarding.shipping_required') as string)
      return
    }

    OnboardingApi.saveShipping(
      {
        shipping_method: 'vendor',
        shipping_type: form.shipping_type,
        amount: Number(form.amount) || 0,
        shipping_fixed_price:
          form.shipping_type === 'free_above_x_value'
            ? Number(form.shipping_fixed_price) || 0
            : 0,
        from,
        to,
        unit: form.unit,
        shipping_policy_page_title: form.shipping_policy_page_title || 'Shipping Policy',
        shipping_policy_page_url: form.shipping_policy_page_url || 'https://koninstore.com',
      },
      () => onSuccess(),
      setIsSaving
    )
  }

  if (isLoading) {
    return (
      <Stack alignItems='center' py={6}>
        <CircularProgress />
      </Stack>
    )
  }

  return (
    <Stack gap={2}>
      <Typography fontWeight={600} fontSize={18}>
        {t('onboarding.step2_title') as string}
      </Typography>
      <Typography fontSize={14} color='text.secondary'>
        {t('onboarding.step2_subtitle') as string}
      </Typography>
      {error ? <Alert severity='warning'>{error}</Alert> : null}

      <TextField
        select
        label={t('onboarding.shipping_method') as string}
        value={form.shipping_type}
        onChange={(e) => setForm({...form, shipping_type: e.target.value})}
        fullWidth
      >
        <MenuItem value='free'>{t('FreeShipping') as string}</MenuItem>
        <MenuItem value='free_above_x_value'>{t('FreeshippingaboveXamount') as string}</MenuItem>
        <MenuItem value='fixed'>{t('FixedPriceShipping') as string}</MenuItem>
      </TextField>

      {(form.shipping_type === 'fixed' || form.shipping_type === 'free_above_x_value') && (
        <TextField
          label={t('onboarding.shipping_cost') as string}
          value={form.shipping_type === 'free_above_x_value' ? form.shipping_fixed_price : form.amount}
          onChange={(e) =>
            form.shipping_type === 'free_above_x_value'
              ? setForm({...form, shipping_fixed_price: e.target.value})
              : setForm({...form, amount: e.target.value})
          }
          fullWidth
          type='number'
        />
      )}

      {form.shipping_type === 'free_above_x_value' ? (
        <TextField
          label={t('invoiceAmount') as string}
          value={form.amount}
          onChange={(e) => setForm({...form, amount: e.target.value})}
          fullWidth
          type='number'
        />
      ) : null}

      <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
        <TextField
          label={t('from') as string}
          value={form.from}
          onChange={(e) => setForm({...form, from: e.target.value})}
          fullWidth
          type='number'
        />
        <TextField
          label={t('to') as string}
          value={form.to}
          onChange={(e) => setForm({...form, to: e.target.value})}
          fullWidth
          type='number'
        />
        <TextField
          select
          label={t('onboarding.delivery_unit') as string}
          value={form.unit}
          onChange={(e) => setForm({...form, unit: e.target.value})}
          fullWidth
        >
          <MenuItem value='days'>{t('day') as string}</MenuItem>
          <MenuItem value='hours'>{t('hours') as string}</MenuItem>
        </TextField>
      </Stack>

      <Box display='flex' justifyContent='flex-end'>
        <Button variant='contained' disabled={isSaving} onClick={submit}>
          {isSaving ? <CircularProgress size={20} color='inherit' /> : (t('onboarding.continue') as string)}
        </Button>
      </Box>
    </Stack>
  )
}
